import { resolveErrorContent, useTranslation, type TranslatedString } from "@i18n";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "../../services/axiosWrapper";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import type { LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import "./viewCss.css";
import { showToastNotification } from "../../reducers/toastSlice";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "../../services/constants";

import Accordion from "../../cubeComponent/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import CircularProgress from "@mui/material/CircularProgress";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconComponent from "@CubeComponents/icons";
import LayoutRow, { type LayoutItem } from "./LayoutRow";
import type { RootState } from "@Reducers/index";
import { LAYOUT_MENU_ACTION, LAYOUT_ERROR_CODE, getLayoutViewKey } from "@Helpers/constants";
import type { DropdownItem } from "./types";

// Ids for the header "Create" menu options; the `=== SHARED` checks below also drive the my-vs-shared split.
/**
 * Ids of the header "Create" menu options. Exported because a caller passing `createOptions` has to
 * emit the SAME ids — `onCreateClick` derives `isShared` by comparing against `SHARED` — and a
 * second copy of these literals in the caller could silently drift.
 */
export const CREATE_LAYOUT_OPTION = { MY: "myLayout", SHARED: "sharedLayout" } as const;

/**
 * The saved view as the layout callbacks hand it back. Every member is optional and `data` is
 * `unknown` so that each consumer's own view type — `LayoutData` here, `ViewerLayout` in
 * features/design-viewer — is assignable to this one.
 */
type LayoutCallbackData = Partial<Omit<LayoutData, "data">> & { data?: unknown };

interface Props {
  layoutData: LayoutData[];
  currentDataId?: string | undefined;
  /**
   * METHOD shorthand + an all-optional payload, for this and the two callbacks below.
   * DesignViewerLayoutManager annotates its handlers with its own `ViewerLayout` (a `LayoutData`
   * whose `data` blob is narrowed) while ProjectLayouts uses `LayoutData`. A property arrow made
   * the parameter contravariant, so neither fitted and these were `any`; method syntax makes it
   * bivariant, and because every member of `LayoutCallbackData` is optional, BOTH `ViewerLayout`
   * and `LayoutData` are assignable *to* it — which is the direction bivariance can use.
   */
  onClickLayout(data: LayoutCallbackData): void;
  onEditCallback?(args: { layoutData?: LayoutCallbackData; layoutDataIndex?: number }): void;
  onRenameCallback?(args: { renameData?: LayoutCallbackData; renameDataIndex?: number }): void;
  onDuplicateCallback?: (args: { duplicatedData: LayoutData; layoutDataIndex: number }) => void;
  onDeleteCallback?: (args: { deleteData: LayoutData; layoutDataIndex: number }) => Promise<void> | void;
  setLayoutData: React.Dispatch<React.SetStateAction<LayoutData[]>>;
  headerName?: string;
  enableUpdate?: boolean;
  name?: string;
  /** Returns the caller-specific `data` blob for a new view; its shape varies by resourceType. */
  createNewLayoutData: () => Record<string, unknown>;
  /** Extra scope keys (projectId, contractId, ...) merged into the create payload. */
  newData?: Record<string, unknown>;
  /**
   * When provided, selecting an option from the header "Create" menu ("My Layout" / "Shared Layout")
   * invokes this callback (with `isShared`) instead of the built-in create-on-select flow. Use it to
   * open a custom create modal (e.g. a module picker) that performs the createView call itself.
   * Existing usages omit it and keep the default behavior.
   */
  onCreateClick?: (isShared: boolean) => void;
  /**
   * When provided, returns the dropdown menu items for a given layout row (overrides the default
   * Rename/Duplicate/Delete set). Lets callers vary the menu per row, e.g. shared vs. my layouts.
   * Supported item ids: "edit", "rename", "delete", "duplicate", "moveToShared", "duplicateAsMy".
   */
  getDropDownItems?: (item: LayoutData) => DropdownItem[];
  /**
   * While the caller's view fetch is in flight. Without it an empty `layoutData` is
   * indistinguishable from "no views", so both accordions render the "No Data" empty state and then
   * flip to the list once the response lands.
   */
  isLoading?: boolean;
  /**
   * Overrides the header "Create" menu options. Callers whose create permissions vary per option use
   * this to drop the ones the user may not use; omit it to keep the default My / Shared pair.
   */
  createOptions?: { text: string; id: string }[];
  /**
   * Names a duplicate/copy of `source`. Use it where the backend enforces name uniqueness — copying
   * the source name verbatim (the default) would then always collide with the row being copied.
   * `isShared` is the namespace the copy is going INTO, which is not necessarily the source's.
   */
  getDuplicateName?: (source: LayoutData, isShared: boolean) => string;
}

function Index({
  layoutData,
  currentDataId,
  onClickLayout,
  onEditCallback,
  onRenameCallback,
  onDuplicateCallback,
  onDeleteCallback,
  setLayoutData,
  headerName = "Layouts",
  enableUpdate = false,
  name = "Layout",
  createNewLayoutData,
  isLoading = false,
  newData = {},
  onCreateClick,
  getDropDownItems,
  createOptions,
  getDuplicateName,
}: Props) {
  const { t } = useTranslation();
  const [renameData, setRenameData] = useState<Partial<LayoutData>>({});
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const dispatch = useDispatch();

  // The reference is load-bearing, not an allocation saving: this array reaches `SelectDropDown`'s
  // `items`, and that component re-seeds its own option state from a render-phase latch on the items
  // reference (`Object.is`), so a fresh array on every render would re-seed it on every render.
  const dropDownItems = useMemo<DropdownItem[]>(() => {
    const rowItems: DropdownItem[] = [
      {
        text: t("Rename"),
        id: LAYOUT_MENU_ACTION.RENAME,
        iconName: "editProfile",
      },
      {
        text: t("Duplicate"),
        id: LAYOUT_MENU_ACTION.DUPLICATE,
        iconName: "copy",
      },
      {
        text: t("Delete"),
        id: LAYOUT_MENU_ACTION.DELETE,
        iconName: "delete",
      },
    ];
    if (!enableUpdate) {
      return rowItems;
    }
    return [
      {
        text: t("Update"),
        id: LAYOUT_MENU_ACTION.EDIT,
        iconName: "SettingsIcon",
      },
      ...rowItems,
    ];
  }, [enableUpdate, t]);

  function handleSelectedOption({
    args,
    layoutData,
    layoutDataIndex,
  }: {
    args: SelectEventArgs;
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    if (args.item.id == LAYOUT_MENU_ACTION.EDIT) {
      onEditCallback?.({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.RENAME) {
      setRenameData(layoutData);
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DELETE) {
      // fire-and-forget: sync dropdown dispatcher; each action toasts and updates state itself.
      void onClickOptionDelete({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DUPLICATE) {
      void onClickOptionDuplicate({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.MOVE_TO_SHARED) {
      void moveMyLayoutToShared({ layoutData, layoutDataIndex });
    }
    if (args.item.id == LAYOUT_MENU_ACTION.DUPLICATE_AS_MY) {
      void copySharedLayoutToMyLayout({ layoutData, layoutDataIndex });
    }
  }

  /**
   * Reports a refused layout write.
   *
   * A permission refusal, a name clash or an immutable system layout each say something specific and
   * actionable, and collapsing all of them into "Something went wrong" is what made them
   * undiagnosable. The server no longer sends prose for any of them: the four structural refusals
   * carry a string LAYOUT_ERROR_CODE and are worded here, in the user's language, and every other
   * refusal carries a numeric code `resolveErrorContent` translates from the mapper. The generic
   * message is the last resort for a body carrying neither.
   */
  const LAYOUT_CODE_MESSAGES: Record<string, TranslatedString> = {
    [LAYOUT_ERROR_CODE.IN_USE_BY_OTHERS]: t("The Layout cannot be deleted as it is already in use."),
    [LAYOUT_ERROR_CODE.NAME_ALREADY_EXISTS]: t("A layout with this name already exists."),
    [LAYOUT_ERROR_CODE.SYSTEM_LAYOUT_IMMUTABLE]: t("System layouts cannot be renamed, configured or deleted."),
    [LAYOUT_ERROR_CODE.SHARED_PERMISSION_DENIED]: t("You do not have permission to perform this action."),
  };

  const reportFailure = (data: { errorCode?: number | string | undefined }) => {
    const { errorCode } = data;
    const content =
      typeof errorCode === "string"
        ? (LAYOUT_CODE_MESSAGES[errorCode] ?? t("Something went wrong"))
        : errorCode != null
          ? resolveErrorContent({ status: false, errorCode })
          : t("Something went wrong");
    dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
  };

  // Convert a "my" layout into a shared one (keeps it in place, moves it to the Shared section).
  async function moveMyLayoutToShared({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    if (!layoutData._id) return;
    const response = await axiosWrapper.api.post("/layoutView/updateView", {
      updateData: { isShared: true },
      id: layoutData._id,
    });
    if (!response.data.status) {
      reportFailure(response.data);
      return;
    }
    setLayoutData((prevState) => {
      const newState = [...prevState];
      if (newState[layoutDataIndex]) {
        newState[layoutDataIndex] = { ...newState[layoutDataIndex], isShared: true };
      }
      return newState;
    });
  }

  // Copy a shared layout into the current user's "my" layouts.
  async function copySharedLayoutToMyLayout({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.api.post("/layoutView/createView", {
      createData: {
        ...layoutData,
        isShared: false,
        ...(getDuplicateName ? { name: getDuplicateName(layoutData, false) } : {}),
      },
    });
    if (!response.data.status) {
      reportFailure(response.data);
      return;
    }
    // Read off the narrowed success arm here: the property narrowing does not survive into the
    // updater callback below.
    const created = response.data.result;
    setLayoutData((prevState) => [...prevState, created]);
    onDuplicateCallback?.({ duplicatedData: layoutData, layoutDataIndex });
  }

  async function onClickOptionDelete({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    if (!layoutData._id) return;
    const response = await axiosWrapper.api.post("/layoutView/deleteView", {
      id: layoutData._id,
    });
    if (!response.data.status) {
      reportFailure(response.data);
      return;
    }
    setLayoutData((prevState) => {
      const newState = [...prevState];
      newState.splice(layoutDataIndex, 1);
      return newState;
    });
    onDeleteCallback && (await onDeleteCallback({ deleteData: layoutData, layoutDataIndex }));
  }

  async function onClickOptionDuplicate({
    layoutData,
    layoutDataIndex,
  }: {
    layoutData: LayoutData;
    layoutDataIndex: number;
  }) {
    const response = await axiosWrapper.api.post("/layoutView/createView", {
      createData: {
        ...layoutData,
        ...(getDuplicateName ? { name: getDuplicateName(layoutData, layoutData.isShared === true) } : {}),
      },
    });
    if (!response.data.status) {
      reportFailure(response.data);
      return;
    }
    const created = response.data.result;
    setLayoutData((prevState) => [...prevState, created]);
    onDuplicateCallback?.({
      duplicatedData: layoutData,
      layoutDataIndex,
    });
  }

  const createData = (isShared: boolean) => {
    const sharedName = isShared ? "Shared " : "";
    return {
      resourceType: "VIEWER",
      data: createNewLayoutData(),
      name: `${sharedName}${name} ${
        isShared
          ? layoutData.filter((item: LayoutData) => item.isShared).length + 1
          : layoutData.filter((item: LayoutData) => !item.isShared).length + 1
      }`,
      ...newData,
    };
  };

  const handleTickClick = async () => {
    // `renameData` is `{}` while no row is being renamed; there is no view to update then.
    if (!renameData._id) return;
    await axiosWrapper.api.post("/layoutView/updateView", {
      updateData: {
        name: renameData.name,
      },
      id: renameData._id,
    });

    const itemIndex = layoutData.findIndex((item) => item._id === renameData._id);

    setRenameData({});
    setLayoutData((prevState) => {
      if (itemIndex < 0) {
        return prevState;
      }
      const tempData = [...prevState];
      if (!tempData[itemIndex]) {
        return prevState;
      }
      tempData[itemIndex].name = renameData.name;
      return tempData;
    });

    onRenameCallback?.({
      renameData,
      renameDataIndex: itemIndex,
    });
  };

  function layoutContent(showShared: boolean) {
    const layout: LayoutItem[] = [];
    layoutData.forEach((item: LayoutData, index: number) => {
      if (showShared ? item.isShared : !item.isShared) {
        layout.push({
          ...item,
          index,
        });
      }
    });
    // A caller-supplied getDropDownItems owns the whole decision; otherwise keep the legacy rule
    // (own non-default rows only), expressed as "no items" so an empty list always means no menu.
    const itemsForRow = (item: LayoutItem): DropdownItem[] => {
      if (getDropDownItems) return getDropDownItems(item);
      return !item.isDefault && userDetails._id === item.createdBy ? dropDownItems : [];
    };
    return (
      <div className="cursor-pointer font-normal">
        {layout.length > 0 ? (
          layout.map((item: LayoutItem) => (
            <LayoutRow
              key={getLayoutViewKey(item)}
              item={item}
              renameData={renameData}
              // `LayoutRow`'s `currentDataId` is `string?` (the key may be ABSENT) while this prop
              // reads `string | undefined`; the row only compares it, so it is spread when set.
              {...(currentDataId !== undefined && { currentDataId })}
              dropDownItems={itemsForRow(item)}
              onClickLayout={onClickLayout}
              handleSelectedOption={handleSelectedOption}
              setRenameData={setRenameData}
              onTickClick={handleTickClick}
            />
          ))
        ) : (
          <div className="flex flex-col w- items-center justify-center">
            <IconComponent name="nodatasvg" width="8rem" height="8rem" />
            <div className="text-12">{t("No Data")}</div>
          </div>
        )}
      </div>
    );
  }

  async function onSelectCreate(args: SelectEventArgs) {
    const isShared = args.item.id === CREATE_LAYOUT_OPTION.SHARED;
    const data = createData(isShared);

    if (!Array.isArray(data) && typeof data === "object") {
      const created = await axiosWrapper.api.post("/layoutView/createView", {
        createData: { ...data, isShared },
      });
      if (created.data.status) {
        const createdView = created.data.result;
        setLayoutData((prevState) => [...prevState, createdView]);
        dispatch(
          showToastNotification({
            content: t("designviewer_layout_created", { layoutName: createdView.name }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    }
  }

  return (
    <div className="flex flex-col overflow-hidden">
      <div
        className="flex justify-between px-2 py-3 mb-4 items-center border-b border-border_light"
        data-testid="addNewLayout"
      >
        <div className="text-2xl font-semibold leading-[1.2] text-primary_text" data-testid="LayoutsHeading">
          {headerName}
        </div>
        <SelectDropDown
          data-testid="createBtn"
          items={
            createOptions ?? [
              {
                text: "My Layout",
                id: CREATE_LAYOUT_OPTION.MY,
              },
              {
                text: "Shared Layout",
                id: CREATE_LAYOUT_OPTION.SHARED,
              },
            ]
          }
          select={
            onCreateClick
              ? (args: SelectEventArgs) => onCreateClick(args.item.id === CREATE_LAYOUT_OPTION.SHARED)
              : onSelectCreate
          }
          placeholder={t("Create")}
          buttonType="gradientButton"
        />
      </div>
      <div className="max-h-[25rem] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16" data-testid="layoutsLoader">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            <Accordion defaultExpanded data-testid="myLayoutsSection">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="myLayoutsHeader">
                <div
                  className="flex justify-between items-center px-2 py-3 text-base font-bold leading-tight text-primary_text"
                  data-testid="myLayouts"
                >
                  {`My ${headerName}`}
                </div>
              </AccordionSummary>
              <AccordionDetails data-testid="myLayoutsContent">{layoutContent(false)}</AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded data-testid="sharedLayoutsSection">
              <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="sharedLayoutsHeader">
                <div
                  className="flex justify-between items-center px-2 py-3 text-base font-bold leading-tight text-primary_text"
                  data-testid="sharedLayouts"
                >
                  {`Shared ${headerName}`}
                </div>
              </AccordionSummary>
              <AccordionDetails data-testid="sharedLayoutsContent">{layoutContent(true)}</AccordionDetails>
            </Accordion>
          </>
        )}
      </div>
    </div>
  );
}

export default Index;
